import ScratchJr from '../ScratchJr';
import BlockSpecs from '../blocks/BlockSpecs';
import Alert from './Alert';
import Palette from './Palette';
import Thumbs from './Thumbs';
import UI from './UI';
import Page from '../engine/Page';
import Sprite from '../engine/Sprite';
import OS from '../../tablet/OS';
import IO from '../../tablet/IO';
import Paint from '../../painteditor/Paint';
import SVG2Canvas from '../../utils/SVG2Canvas';
import ActivityController from '../activity/engine/ActivityController';
import ActivityPluginRegistry from '../activity/engine/ActivityPluginRegistry';
import ActivityUI from '../activity/shared/ActivityUI';
import ChallengePlugin from '../activity/plugins/challenge/ChallengePlugin';
import GuidedCreationPlugin from '../activity/plugins/guided-creation/GuidedCreationPlugin';
import {frame, gn, newHTML, scaleMultiplier, getIdFor,
    isAndroid, setProps, setCanvasSize} from '../../utils/lib';

let metadata = undefined;
let mediaCount = -1;
let saving = false;
let interval = undefined;
let pageid;
let loadIcon = undefined;
let error = false;
let projectbarsize = 66;
let mediaCountBase = 1;
let activeActivityController = null;
let activeActivityUI = null;
let activityRunMonitor = null;
let activityRunObservedExecution = false;

export default class Project {
    static hasActiveChallengeActivity () {
        return Boolean(
            activeActivityController &&
            activeActivityController.stateMachine &&
            activeActivityController.stateMachine.activityType === 'challenge'
        );
    }

    static runActiveChallengeActivity (evt) {
        if (!Project.hasActiveChallengeActivity()) {
            return false;
        }

        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }

        const controller = activeActivityController;
        const stateMachine = controller.stateMachine;
        const shouldResetSprites = !controller.plugin ||
            typeof controller.plugin.shouldResetSpritesBeforeRun !== 'function' ||
            controller.plugin.shouldResetSpritesBeforeRun();

        if (!ScratchJr.runtime.inactive()) {
            return Project.stopActiveChallengeRun(evt);
        }

        ScratchJr.unfocus(evt);

        if (stateMachine.state === 'READY') {
            controller.startActivity();
        } else if (stateMachine.state === 'SUCCESS' || stateMachine.state === 'FAILED') {
            controller.retry();
        } else if (stateMachine.state === 'VALIDATING' || stateMachine.state === 'LOADING') {
            return true;
        }

        controller.run();
        activityRunObservedExecution = false;
        ScratchJr.stopStrips();
        if (controller.plugin && typeof controller.plugin.captureRunStartPosition === 'function') {
            controller.plugin.captureRunStartPosition();
        }
        ScratchJr.startGreenFlagThreads(shouldResetSprites);
        Project.startActivityRunMonitor();
        return true;
    }

    static stopActiveChallengeRun (evt) {
        if (!Project.hasActiveChallengeActivity()) {
            return false;
        }

        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }

        if (activityRunMonitor) {
            clearInterval(activityRunMonitor);
            activityRunMonitor = null;
        }
        activityRunObservedExecution = false;
        ScratchJr.unfocus(evt);
        ScratchJr.stopStrips();

        if (activeActivityController && activeActivityController.stateMachine.state === 'RUNNING') {
            activeActivityController.retry();
        }
        return true;
    }

    static recordActiveChallengeReset () {
        if (activeActivityUI && typeof activeActivityUI.recordReset === 'function') {
            activeActivityUI.recordReset();
        }
    }

    static restartActiveChallengeActivity (evt, opts = {}) {
        if (!Project.hasActiveChallengeActivity()) {
            return false;
        }

        if (evt) {
            evt.preventDefault();
            evt.stopPropagation();
        }

        if (activityRunMonitor) {
            clearInterval(activityRunMonitor);
            activityRunMonitor = null;
        }
        activityRunObservedExecution = false;
        ScratchJr.unfocus(evt);
        ScratchJr.stopStrips();
        ScratchJr.resetSprites();

        const controller = activeActivityController;
        const plugin = controller && controller.plugin;
        const restartHandled = plugin && typeof plugin.restartActivity === 'function'
            ? plugin.restartActivity()
            : false;

        if (!restartHandled && controller) {
            controller.retry();
        }

        return true;
    }

    static get metadata () {
        return metadata;
    }

    static set metadata (newMetadata) {
        metadata = newMetadata;
    }

    static get mediaCount () {
        return mediaCount;
    }

    static set mediaCount (newMediaCount) {
        mediaCount = newMediaCount;
    }

    static set loadIcon (newLoadIcon) {
        loadIcon = newLoadIcon;
    }

    static get loadIcon () {
        return loadIcon;
    }

    static get saving () {
        return saving;
    }

    static set saving (newSaving) {
        saving = newSaving;
    }

    static get error () {
        return error;
    }


    static clear () {
        ScratchJr.stage.clear();
        UI.clear();
    }

    static load () {
        mediaCountBase = 1;
        ScratchJr.log('Project load status', ScratchJr.getTime(), 'sec', BlockSpecs.loadCount);
        if (BlockSpecs.loadCount > 0) {
            setTimeout(function () {
                Project.delayLoad();
            }, 32);
        } else {
            Project.startLoad();
        }
    }

    static delayLoad () {
        if (BlockSpecs.loadCount < 1) {
            Project.startLoad();
        } else {
            setTimeout(function () {
                Project.delayLoad();
            }, 32);
        }
    }

    static startLoad () {
        ScratchJr.log('all UI assets recieved - procced to call server', ScratchJr.getTime(), 'sec');
        Project.setProgress(20);
        UI.layout();

        // Issue #219 Fix: Add initialization guard to prevent race condition
        if (!window.currentProjectId) {
            console.warn('Project.load: currentProjectId not initialized, using default project');
            // Set to default project to prevent undefined being passed to IO.getObject
            window.currentProjectId = ScratchJr.defaultProject || 'defaultproject';
        }

        // Additional validation to ensure currentProjectId is a valid string
        if (typeof window.currentProjectId !== 'string' || window.currentProjectId.length === 0) {
            console.error('Project.load: Invalid currentProjectId value:', window.currentProjectId);
            window.currentProjectId = 'defaultproject';
        }

        // Keep the ScratchJr module state aligned with the effective project id,
        // including the default-project fallback used by bare editor loads.
        ScratchJr.currentProject = window.currentProjectId;

        IO.getObject(window.currentProjectId, Project.dataRecieved);
    }

    static dataRecieved (str) {
        ScratchJr.log('got project metadata', ScratchJr.getTime(), 'sec');

        // Issue #201 Fix: Add defensive handling for null/invalid responses
        if (!str || typeof str !== 'string') {
            console.error('Project.dataRecieved: Invalid or null data received:', str);
            // Initialize with default empty project to prevent crash
            mediaCount = 0;
            new Page(getIdFor('page'));
            Palette.selectCategory(1);
            return;
        }

        let parsedData;
        try {
            parsedData = JSON.parse(str);
        } catch (error) {
            console.error('Project.dataRecieved: Failed to parse JSON:', error, 'Raw data:', str);
            // Initialize with default empty project to prevent crash
            mediaCount = 0;
            new Page(getIdFor('page'));
            Palette.selectCategory(1);
            return;
        }

        // Issue #382 Fix: Handle both legacy array format and new database response format
        let dataArray;
        if (Array.isArray(parsedData)) {
            // Legacy format: direct array
            dataArray = parsedData;
        } else if (parsedData && parsedData.rows && Array.isArray(parsedData.rows)) {
            // New database format: {status: 'ok', rows: Array}
            dataArray = parsedData.rows;
        } else {
            console.error('Project.dataRecieved: Invalid parsed data structure:', parsedData);
            console.error('Project.dataRecieved: Expected array or {rows: Array} format');
            // Initialize with default empty project to prevent crash
            mediaCount = 0;
            new Page(getIdFor('page'));
            Palette.selectCategory(1);
            return;
        }

        if (dataArray.length === 0) {
            console.log('Project.dataRecieved: No project data found');

            // DUPLICATION GUARD: If we navigated with a concrete project id (e.g., proj_...)
            // the DatabaseModule cache may not be ready yet. Retry a few times before
            // creating a brand new project to avoid duplicate creations.
            try {
                const id = window.currentProjectId;
                const looksLikeProjectId = typeof id === 'string' && /^proj_\d{10,14}_[a-z0-9]{5,}/i.test(id);
                const canRetry = (typeof window !== 'undefined');
                if (canRetry && looksLikeProjectId) {
                    window.__PROJECT_LOAD_RETRIES__ = window.__PROJECT_LOAD_RETRIES__ || {};
                    const tries = window.__PROJECT_LOAD_RETRIES__[id] || 0;
                    if (tries < 10) { // up to ~1s with 100ms delay
                        window.__PROJECT_LOAD_RETRIES__[id] = tries + 1;
                        console.log(`⏳ Retrying project fetch for ${id} (attempt ${tries + 1}/10)`);
                        setTimeout(function () {
                            IO.getObject(id, Project.dataRecieved);
                        }, 100);
                        return;
                    } else {
                        console.warn('⚠️ Max retries reached for', id, '- proceeding to create default project');
                    }
                }
            } catch (_) { /* proceed to create default project */ }

            console.log('Project.dataRecieved: Initializing default project');

            // Initialize with default empty project
            mediaCount = 0;
            new Page(getIdFor('page'));
            Palette.selectCategory(1);
            // On Android 4.2, this comes up blank the first time, so try again in 100ms.
            setTimeout(function () {
                Palette.selectCategory(1);
            }, 100);

            // CRITICAL FIX (Issue #559): Wait for page to be fully initialized before creating project in DB
            // The page creation is async (createCat increments mediaCount), so we need to wait
            // Otherwise Project.getProject() returns incomplete data with no sprites
            function createProjectWhenReady() {
                // Wait for sprite creation to complete (mediaCount goes back to 0)
                if (mediaCount > 0) {
                    console.log('⏳ Waiting for page initialization... mediaCount:', mediaCount);
                    setTimeout(createProjectWhenReady, 100);
                    return;
                }

                console.log('💾 Page ready, creating new project in database...');

                // CRITICAL FIX (Issue #607): Use incremental project naming
                // Get unique project name using IO.uniqueProjectName to match native behavior
                // This ensures projects get names like "Project 1", "Project 2", etc.
                // instead of all being named "My Project"
                IO.uniqueProjectName({
                    name: 'Project'
                }, function(nameData) {
                    var projectName = nameData.name;
                    var projectId = window.currentProjectId;
                    console.log('🏷️  Generated unique project name:', projectName);

                    // CRITICAL FIX (Issue #607): Reserve name in cache IMMEDIATELY
                    // This prevents race condition where multiple projects get the same name
                    // Add placeholder to cache before calling IO.createProject
                    if (window.dbModule && window.dbModule.projectCache) {
                        const placeholder = {
                            id: projectId,
                            name: projectName,
                            profileId: 'default-profile',
                            accountId: null,
                            json: '{}',  // Placeholder
                            thumbnail: '{}',  // Placeholder
                            createdAt: Date.now(),
                            lastModified: Date.now(),
                            version: ScratchJr.version || 'iOSv01',
                            metadata: {
                                isgift: '0',
                                deleted: 'NO'
                            },
                            size: 0,
                            _placeholder: true  // Mark as placeholder
                        };
                        window.dbModule.projectCache.set(projectId, placeholder);
                        console.log('🔒 RESERVED name in cache:', projectName, 'for project:', projectId);
                        console.log('🔒 Cache size after reservation:', window.dbModule.projectCache.size);
                    }

                    metadata = {
                        id: projectId, // Include ID for explicit INSERT
                        name: projectName,  // Use incremental name instead of hardcoded "My Project"
                        version: ScratchJr.version || 'iOSv01',  // FIX: Default to 'iOSv01' if version is undefined
                        isgift: '0',
                        deleted: 'NO',
                        json: Project.getProject(ScratchJr.stage.pages[0].id),
                        thumbnail: {
                            pagecount: 1,
                            md5: 'backgrounds/Farm.svg'
                        }
                    };

                    // Debug: Log what we're about to save
                    console.log('📦 Project data to save:', {
                        id: metadata.id,
                        name: metadata.name,
                        pages: metadata.json.pages,
                        sprites: metadata.json[metadata.json.pages[0]] ? metadata.json[metadata.json.pages[0]].sprites : 'N/A'
                    });

                    IO.createProject(metadata, function(result) {
                        console.log('✅ New project created in database:', result);
                        // FIX Issue #559: Clear changed flag immediately, not after delay
                        // This prevents race condition where user changes during delay get cleared
                        ScratchJr.changed = false; // Mark as saved immediately

                        // CRITICAL FIX: Must call liftCurtain for new projects, otherwise loading screen stays stuck
                        setTimeout(function() {
                            Project.liftCurtain();
                            UI.needsScroll();
                            console.log('🎭 Curtain lifted for new project');
                        }, 2000); // Match the delay used for existing projects
                    });
                }, true); // Pass true as useOne parameter to ensure "Project 1" instead of just "Project"
            }

            // Start checking for page readiness
            setTimeout(createProjectWhenReady, 200);
            return;
        }

        var data = dataArray[0];
        metadata = IO.parseProjectData(data);
        mediaCount = -1;
        if (metadata.json) {
            Project.loadData(metadata.json, doneProjectLoad);
        } else {
            mediaCount = 0;
            new Page(getIdFor('page'));
            Palette.selectCategory(1);
            // On Android 4.2, this comes up blank the first time, so try again in 100ms.
            setTimeout(function () {
                Palette.selectCategory(1);
            }, 100);
            Project.loadwait(doneProjectLoad);
        }
        function doneProjectLoad () {
            // Clear gift flag
            if ('id' in metadata) {
                metadata.isgift = '0';
                IO.setProjectIsGift(metadata);
            }
            Palette.selectCategory(1);
            // On Android 4.2, this comes up blank the first time, so try again in 100ms.
            setTimeout(function () {
                Palette.selectCategory(1);
            }, 100);
            Paint.layout();
            Project.setProgress(100);
            // FIX Issue #559: Clear changed flag immediately, not after delay
            ScratchJr.changed = false;
            ScratchJr.storyStarted = false;

            // Allow enhanced loading animation to display before transitioning (reduced to 2 seconds)
            setTimeout(function() {
                Project.liftCurtain();
                ScratchJr.stage.currentPage.update();
                UI.needsScroll();
                ScratchJr.log('all thumbnails updated', ScratchJr.getTime(), 'sec');
                if (isAndroid) {
                    AndroidInterface.notifyEditorDoneLoading();
                }
                if (ScratchJr.editmode === 'activity' || ScratchJr.editmode === 'challenge') {
                    Project.initActivityExperience();
                } else {
                    Project.destroyActivityExperience();
                }
            }, 2000); // 3 second delay to show enhanced loading animation fully
        }
    }


    static init () {
        ScratchJr.log('Project init', ScratchJr.getTime(), 'sec');
        // Backdrop and penguin already created in ScratchJr.js - just get reference
        var bd = gn('backdrop');
        console.log('📝 Project.init: Backdrop and penguin already created in ScratchJr.js');
        
        var modalOuter = newHTML('div', 'modal-outer', frame.parentNode);
        var modalMiddle = newHTML('div', 'modal-middle', modalOuter);
        var modal = newHTML('div', 'modal hide fade', modalMiddle);
        modal.setAttribute('id', 'modaldialog');
        setProps(gn('modaldialog').style, {});
        var body = newHTML('div', 'modal-body', modal);
        body.setAttribute('id', 'modalbody');
        setProps(body.style, {
            zoom: scaleMultiplier
        });
        
        // Show enhanced loading immediately - don't wait for loadIcon
        Project.addFeedback();
        Project.drawBlind();
    }

    static addFeedback () {
        var body = gn('modalbody');
        var modal = gn('modaldialog');
        var backdrop = gn('backdrop');
        
        console.log('🎯 addFeedback called');
        console.log('  - body:', body);
        console.log('  - modal:', modal);
        console.log('  - backdrop:', backdrop);
        
        // Penguin and text already created in init() - no need to recreate
        console.log('📝 Penguin and text already created in init() function');
        
        // Now handle modal visibility after penguin is already created
        if (backdrop) {
            backdrop.className = backdrop.className.replace('fade', 'fade in');
            backdrop.style.display = 'block';
            console.log('🌟 Backdrop made visible with "in" class');
        }
        
        if (modal) {
            modal.className = modal.className.replace('fade', 'fade in');
            modal.style.display = 'block';
            console.log('🌟 Modal made visible with "in" class');
        }
        
        // Add floating particles to backdrop (full screen) - Option #8 style with many more particles
        if (backdrop) {
            for (var i = 0; i < 30; i++) {
                var particle = newHTML('div', 'loading-particle', backdrop);
                // Add positioning manually to ensure particles show - spread across entire screen
                particle.style.position = 'absolute';
                particle.style.top = (Math.random() * 95 + 2) + '%';
                particle.style.left = (Math.random() * 95 + 2) + '%';
                console.log('✨ Created particle', i, 'at', particle.style.top, particle.style.left);
            }
            console.log('🌟 Added', 30, 'particles to backdrop');
            
            // Penguin and text already added immediately at transition start above
            
            // Particle animation with large penguin mascot and loading text
            console.log('🎨 Particle animation with large penguin mascot and loading text');
        } else {
            console.error('❌ No backdrop found - particles not added');
        }
        
        // Keep original elements for compatibility (hidden but present for other functions)
        var cover = newHTML('div', 'loadscreencover', body);
        cover.setAttribute('id', 'progressbar');
        cover.style.display = 'none';
        
        var topcover = newHTML('div', 'topcover', body);
        topcover.setAttribute('id', 'topcover');
        topcover.style.display = 'none';
        
        var cover2 = newHTML('div', 'progressbar2', body);
        cover2.setAttribute('id', 'progressbar2');
        cover2.style.display = 'none';
        
        var li = newHTML('div', 'loadicon', body);
        li.appendChild(loadIcon);
        
        console.log('🎉 Enhanced loading setup complete!');
    }

    static setProgress (perc) {
        if (!gn('progressbar')) {
            return;
        }
        var h = projectbarsize - Math.round(projectbarsize * perc / 100);
        ScratchJr.log('setProgress', perc, h, mediaCount, mediaCountBase);
        gn('progressbar').style.height = h + 'px';
        if (h == 0) {
            gn('progressbar2').style.height = '0px';
            gn('topcover').style.background = '#F9A737';
        }

    }

    static drawBlind () {
        gn('backdrop').setAttribute('class', 'modal-backdrop fade in');
        setProps(gn('backdrop').style, {
            display: 'block'
        });
        setProps(gn('modaldialog').style, {
            display: 'block'
        });
        gn('modaldialog').setAttribute('class', 'modal fade in');
    }

    static loadwait (whenDone) {
        if (interval != null) {
            window.clearInterval(interval);
        }
        mediaCountBase = mediaCount;
        if (mediaCount <= 0) {
            Project.getStarted(whenDone);
        } else {
            interval = window.setInterval(function () {
                Project.loadTask(whenDone);
            }, 32);
        }
    }

    static loadTask (whenDone) {
        if (mediaCount <= 0) {
            Project.getStarted(whenDone);
        } else {
            Project.setProgress(Project.getMediaLoadRatio(70));
        }
    }

    static getMediaLoadRatio (f) {
        if (mediaCount > mediaCountBase) {
            mediaCountBase = mediaCount;
        }
        return 20 + f - (mediaCount / mediaCountBase) * f;
    }

    static getStarted (whenDone) {
        Project.setProgress(90);
        if (interval) {
            window.clearInterval(interval);
        }
        interval = null;
        ScratchJr.log('Project images retrieved from server', ScratchJr.getTime(), 'sec');
        Project.setLoadPage(pageid, whenDone);
        ScratchJr.log('load done', ScratchJr.getTime(), 'sec', '-- media missing = ', mediaCount);
        // CRITICAL FIX (Issue #559): DO NOT reset sprites to home position after loading!
        // Sprites are already initialized with their saved positions from the database.
        // Calling resetPages() here would reset all sprites to default home positions (240, 180),
        // completely ignoring the saved xcoor/ycoor values loaded from IndexedDB.
        // ScratchJr.stage.resetPages();  // ❌ REMOVED - breaks sprite position persistence
        ScratchJr.runtime.beginTimer();
        Project.syncActivityUiAfterLoad();
    }

    static liftCurtain () {
        console.log('🎭 liftCurtain called - starting graceful transition');
        
        // Start the fade out by removing the 'in' class
        gn('backdrop').setAttribute('class', 'modal-backdrop fade');
        gn('modaldialog').setAttribute('class', 'modal fade');
        
        // Wait for CSS transition to complete (CSS transition is 0.15s), then hide
        setTimeout(function() {
            setProps(gn('backdrop').style, {
                display: 'none'
            });
            setProps(gn('modaldialog').style, {
                display: 'none'
            });
            console.log('🎭 Modal completely hidden');
        }, 200);
    }

    static setLoadPage (pageid, whenDone) {
        ScratchJr.log('setLoadPage', ScratchJr.getTime(), 'sec');
        var pages = ScratchJr.stage.getPagesID();
        if (pages.indexOf(pageid) < 0) {
            ScratchJr.stage.currentPage = ScratchJr.stage.pages[0];
        } else {
            ScratchJr.stage.currentPage = ScratchJr.stage.getPage(pageid);
        }
        ScratchJr.stage.currentPage.div.style.visibility = 'visible';
        var list = ScratchJr.stage.pages;
        for (var i = 0; i < list.length; i++) {
            if (ScratchJr.stage.currentPage == list[i]) {
                ScratchJr.stage.currentPage.setPageSprites('visible');
            } else {
                list[i].setPageSprites('hidden');
            }
        }
        try {
            const isActivityMode = typeof window !== 'undefined' &&
                typeof window.location !== 'undefined' &&
                String(window.location.search || '').indexOf('mode=activity') > -1;
            if (isActivityMode && ScratchJr.stage.currentPage && typeof ScratchJr.stage.currentPage.getSprites === 'function') {
                const sprites = ScratchJr.stage.currentPage.getSprites();
                const firstSpriteId = sprites.length > 0 ? sprites[0] : null;
                if (firstSpriteId && gn(firstSpriteId)) {
                    ScratchJr.stage.currentPage.setCurrentSprite(gn(firstSpriteId).owner);
                }
                Palette.selectCategory(0);
                setTimeout(function () {
                    try {
                        Palette.selectCategory(0);
                        Thumbs.updateSprites();
                    } catch (_) {}
                }, 120);
            }
        } catch (_) {}
        if (whenDone) {
            whenDone();
        }
    }

    static syncActivityUiAfterLoad () {
        [0, 250, 800, 1600].forEach((delayMs) => {
            setTimeout(function () {
                try {
                    const controller = window.__activityController;
                    const plugin = controller && controller.plugin ? controller.plugin : null;
                    const flow = plugin && typeof plugin.getActiveFlow === 'function'
                        ? plugin.getActiveFlow('storyWalk')
                        : null;
                    const bandFlow = plugin && typeof plugin.getActiveFlow === 'function'
                        ? plugin.getActiveFlow('bugBand')
                        : null;
                    if (!flow && !bandFlow) {
                        return;
                    }
                    const integratedFlow = bandFlow || flow;
                    if (typeof integratedFlow._syncVisualState === 'function') {
                        integratedFlow._syncVisualState();
                    }
                    if (typeof integratedFlow._stabilizeChildFacingUi === 'function') {
                        integratedFlow._stabilizeChildFacingUi();
                    } else if (typeof integratedFlow._forcePlayerSelection === 'function') {
                        integratedFlow._forcePlayerSelection();
                    }
                } catch (_) {}
            }, delayMs);
        });
    }

    static loadData (data, fcn) {
        try {
            // Parse if string
            const originalData = data;
            data = (typeof data === 'string') ? JSON.parse(data) : data;

            // RESTORE EMBEDDED MEDIA FIRST: Must happen BEFORE unwrapping or any processing
            // This ensures media is in localStorage before sprites are created
            if (data && data.media) {
                Project.restoreEmbeddedMedia(data);
            } else if (data && data.project && data.project.media) {
                // Handle wrapped format
                Project.restoreEmbeddedMedia(data.project);
            }

            // UNWRAP JSON EXPORT FORMAT: Handle wrapped project data from JSON exports
            // JSON exports have structure: { format: "scratchjr-json", project: { ... } }
            // We need the inner project data
            if (data.format === 'scratchjr-json' && data.project) {
                console.log('🔧 Detected JSON export format, unwrapping project data');
                data = data.project;
            }

            // IMPORT COMPATIBILITY FIX: Ensure imported data has all required properties
            Project.fixImportCompatibility(data);

            mediaCount = 0;
            Project.loadme(data, fcn);
            error = false;
        } catch (e) {
            console.log(e); //eslint-disable-line no-console
            var errorMessage = 'Error -- project data corrupted.';

            if (window.reloadDebug) {
                document.write(e.message + '\n' + metadata['json']);
                return;
            }

            Alert.open(frame, gn('flip'), errorMessage, '#ff0000');
            if (interval) {
                window.clearInterval(interval);
            }
            interval = null;
            Palette.selectCategory(1);
            // On Android 4.2, this comes up blank the first time, so try again in 100ms.
            setTimeout(function () {
                Palette.selectCategory(1);
            }, 100);
            
            // Allow enhanced loading animation to display even in error case
            setTimeout(function() {
                Project.liftCurtain();
            }, 2000);
            error = true;
        }
    }

    static loadme (data, fcn) {
        Project.recreate(data);
        Project.loadwait(fcn);
    }

    /**
     * Restore embedded media from project data to localStorage
     * Handles both legacy string format and current object format
     * @param {Object} data - Project data potentially containing media section
     */
    static restoreEmbeddedMedia (data) {
        if (!data || !data.media || typeof data.media !== 'object') {
            return; // No media to restore
        }

        const mediaCount = Object.keys(data.media).length;
        if (mediaCount === 0) {
            return;
        }

        console.log(`📦 Restoring ${mediaCount} embedded assets from project data...`);

        // CRITICAL: Save the media for later preservation
        Project._lastLoadedMedia = data.media;
        console.log(`💾 Preserved media section for future saves`);

        for (const [md5, mediaEntry] of Object.entries(data.media)) {
            // Handle both formats:
            // 1. Direct data URL string (legacy format)
            // 2. Object with svg/png properties (current getProject format)
            let dataURL = null;
            let ext = 'png';

            if (typeof mediaEntry === 'string' && mediaEntry.startsWith('data:')) {
                // Legacy format: direct data URL
                dataURL = mediaEntry;
                if (md5.endsWith('.svg') || dataURL.includes('image/svg')) {
                    ext = 'svg';
                } else if (md5.endsWith('.jpg') || md5.endsWith('.jpeg')) {
                    ext = 'jpg';
                }
            } else if (typeof mediaEntry === 'object' && mediaEntry !== null) {
                // Current format: { svg?: <data>|dataURL, png?: <data>|dataURL }
                // Prefer SVG if available (vector graphics are better quality)
                if (mediaEntry.svg && typeof mediaEntry.svg === 'string') {
                    ext = 'svg';
                    // Accept either raw base64 or data URL
                    dataURL = mediaEntry.svg.startsWith('data:')
                        ? mediaEntry.svg
                        : ('data:image/svg+xml;base64,' + mediaEntry.svg);
                } else if (mediaEntry.png && typeof mediaEntry.png === 'string') {
                    ext = 'png';
                    dataURL = mediaEntry.png.startsWith('data:')
                        ? mediaEntry.png
                        : ('data:image/png;base64,' + mediaEntry.png);
                }
            }

            if (dataURL && (typeof dataURL === 'string')) {
                try {
                    // Ensure we have a data URL
                    if (!dataURL.startsWith('data:')) {
                        const mime = (ext === 'svg') ? 'image/svg+xml' : (ext === 'png' ? 'image/png' : 'image/png');
                        dataURL = `data:${mime};base64,${dataURL}`;
                    }
                    // Store under multiple compatible keys so all loaders can find it
                    const baseKey = `scratchjr_media_${md5}`;
                    const extKey = `scratchjr_media_${md5}.${ext}`;
                    try { localStorage.setItem(baseKey, dataURL); } catch (_) {}
                    try { localStorage.setItem(extKey, dataURL); } catch (_) {}
                    // Older code may consult scratchjr_asset_ for thumbnails/png
                    if (ext === 'png') {
                        try { localStorage.setItem(`scratchjr_asset_${md5}`, dataURL); } catch (_) {}
                        try { localStorage.setItem(`scratchjr_asset_${md5}.png`, dataURL); } catch (_) {}
                    }
                    console.log(`✅ Restored ${ext.toUpperCase()}: ${baseKey} (+${extKey})`);
                } catch (e) {
                    console.error(`❌ Failed to restore ${md5}:`, e.message);
                }
            } else {
                console.warn(`⚠️ Skipping invalid media entry: ${md5}`, mediaEntry);
            }
        }
    }

    static getLoadType (bkgid, sid, cid) {
        if (bkgid != null) {
            return 'bkg';
        }
        if (!cid) {
            return 'none';
        }
        if (sid && cid) {
            return 'modify';
        }
        return 'add';
    }

    //////////////////////////////////////////////////
    // load project data
    //////////////////////////////////////////////////

    /**
     * Fix imported project data to ensure compatibility
     * Prevents crashes during import by adding missing properties
     * @param {Object} data - Project data to fix
     */
    static fixImportCompatibility (data) {
        if (!data) {
            console.warn('🔧 Import compatibility: No data provided');
            return;
        }

        // Issue #203 Fix: Handle both direct project data and gift import nested structure
        // Gift imports wrap project data under 'json' property, direct imports have data at root
        const projectData = data.json || data;  // Use nested data if available, otherwise direct data

        // DEBUG: Log the actual data structure received
        console.log('🔧 Import compatibility - received data structure:');
        console.log('🔧 - data.pages exists:', !!data.pages);
        console.log('🔧 - data.json exists:', !!data.json);
        console.log('🔧 - projectData.pages exists:', !!projectData.pages);
        console.log('🔧 - projectData.pages type:', typeof projectData.pages);
        console.log('🔧 - data keys:', Object.keys(data));
        console.log('🔧 - projectData keys:', Object.keys(projectData));

        if (!projectData.pages) {
            console.warn('🔧 Import compatibility: No pages array found in project data structure');
            console.warn('🔧 Raw data structure keys:', Object.keys(data));
            console.warn('🔧 Project data structure keys:', Object.keys(projectData));
            return;
        }

        // HANDLE CORRUPTED/EMPTY PROJECTS: If pages array is empty, create a default page
        if (projectData.pages.length === 0) {
            console.error('🚨 CORRUPTED PROJECT DETECTED: This project has an empty pages array');
            console.error('🚨 This usually means the project data was corrupted in the database');
            console.error('🚨 Original project content may be lost');
            console.warn('🔧 Creating default page to prevent crashes');
            console.warn('🔧 If you recently exported this project, you can re-import the JSON file to restore it');

            // Note: Browser UX alert removed as requested
            // Users can check console for corruption details

            const defaultPageId = 'page 1';
            projectData.pages = [defaultPageId];
            projectData.currentPage = defaultPageId;
            projectData[defaultPageId] = {
                textstartat: 36,
                sprites: [],
                num: 1,
                lastSprite: null,
                currentSpriteName: null,
                md5: 'backgrounds/Farm.svg', // Default background
                layers: []
            };

            console.log('🔧 Created default page:', defaultPageId);
        }

        console.log('🔧 Applying import compatibility fixes...');

        // VALIDATE CURRENT PAGE: Ensure currentPage exists and has data
        if (projectData.currentPage && !projectData[projectData.currentPage]) {
            console.warn(`🔧 Import compatibility: currentPage "${projectData.currentPage}" has no data, resetting to first page`);
            projectData.currentPage = projectData.pages[0];
        }
        if (!projectData.currentPage && projectData.pages.length > 0) {
            console.warn('🔧 Import compatibility: No currentPage set, defaulting to first page');
            projectData.currentPage = projectData.pages[0];
        }

        // Fix each page
        for (const pageId of projectData.pages) {
            let pageData = projectData[pageId];
            if (!pageData) {
                console.warn(`🔧 Import compatibility: Missing page data for ${pageId}, creating default page`);
                // Create default page data
                pageData = {
                    textstartat: 36,
                    sprites: [],
                    num: projectData.pages.indexOf(pageId) + 1,
                    lastSprite: null,
                    currentSpriteName: null,
                    md5: 'backgrounds/Farm.svg',
                    layers: []
                };
                projectData[pageId] = pageData;
            }

            Project.fixPageImportCompatibility(pageData, pageId);
        }

        // Issue #203 Fix: Apply changes back to original data structure
        // For gift imports, we need to copy the modified projectData back to data.json
        // AND copy essential properties to top-level data for Project.recreate()
        if (data.json) {
            // Gift import format - copy projectData back to data.json
            data.json = projectData;
            // CRITICAL FIX: Copy essential properties to top-level data
            // Project.recreate() expects data.pages, data.currentPage, etc.
            data.pages = projectData.pages;
            data.currentPage = projectData.currentPage;
            // Copy all page data to top-level data
            for (const pageId of projectData.pages) {
                data[pageId] = projectData[pageId];
            }
            console.log('🔧 Applied gift import compatibility fixes to data.json structure and top-level data');
        } else {
            // Direct import format - projectData IS data, so changes are already applied
            console.log('🔧 Applied direct import compatibility fixes to data structure');
        }

        console.log(`✅ Import compatibility fixes applied to ${projectData.pages.length} pages`);
    }

    /**
     * Fix page-specific import compatibility issues
     * @param {Object} pageData - Page data to fix
     * @param {string} pageId - Page identifier
     */
    static fixPageImportCompatibility (pageData, pageId) {
        console.log(`🔧 Fixing import compatibility for page: ${pageId}`);

        // Fix 1: Add currentSpriteName based on lastSprite
        // This prevents "Cannot read properties of undefined (reading 'currentSpriteName')"
        // error in ScratchJr.getSprite() at line 325
        if (pageData.lastSprite && !pageData.currentSpriteName) {
            pageData.currentSpriteName = pageData.lastSprite;
            console.log(`🔧 Added missing currentSpriteName: ${pageData.currentSpriteName}`);
        }

        // Fix 2: Respect user's background choice (including blank)
        // Farm background is only added for NEW projects via Page.emptyPage()
        // Existing projects keep their chosen background, even if blank

        // Fix 3: Ensure sprites array exists
        if (!pageData.sprites) {
            pageData.sprites = [];
            console.log('🔧 Added missing sprites array');
        }

        // Fix 4: Ensure layers array exists
        if (!pageData.layers) {
            pageData.layers = pageData.sprites ? [...pageData.sprites] : [];
            console.log('🔧 Added missing layers array');
        }

        // Fix 5: Ensure num property exists (page number)
        if (!pageData.num) {
            pageData.num = 1;
            console.log('🔧 Added missing page number');
        }

        // Fix 6: Ensure textstartat exists
        if (!pageData.textstartat) {
            pageData.textstartat = 36; // Default text start position
            console.log('🔧 Added missing textstartat property');
        }

        // Fix 7: Remove runtime DOM properties that shouldn't be in saved data
        // The 'div' property causes crashes in Project.setLoadPage at line 317
        // because it tries to access page.div.style.visibility but div is undefined
        if (pageData.div) {
            delete pageData.div;
            console.log('🔧 Removed runtime div property from page data');
        }

        // Fix 8: Remove other runtime properties from sprites
        if (pageData.sprites) {
            for (const spriteName of pageData.sprites) {
                const spriteData = pageData[spriteName];
                if (spriteData && spriteData.div) {
                    delete spriteData.div;
                    console.log(`🔧 Removed runtime div property from sprite: ${spriteName}`);
                }
            }
        }

        console.log(`✅ Import compatibility ensured for page: ${pageId}`);
    }

    static recreate (data) {
        ScratchJr.log('Project data structures start loading', ScratchJr.getTime(), 'sec');
        mediaCount = 0;
        ScratchJr.stage.pages = [];
        var pages = data.pages;
        pageid = data.currentPage;
        for (var i = 0; i < pages.length; i++) {
            Project.recreatePage(pages[i], data[pages[i]]);
        }
        mediaCountBase = mediaCount;
    }

    static recreatePage (name, data, fcn) {
        var page = new Page(name, data, fcn);
        page.div.style.visibility = 'hidden';
    }

    static substractCount () {
        mediaCount--;
        if ((gn('backdrop').className != 'modal-backdrop fade in') || (mediaCountBase == 0)) {
            return;
        }
        Project.setProgress(Project.getMediaLoadRatio(70));
    }

    static recreateObject (page, name, data, callBack, active) {
        console.log(`🔧 Recreating sprite: ${name} with type: ${data.type}`);
        console.log(`🔧 - data.xcoor: ${data.xcoor}, data.ycoor: ${data.ycoor}`);
        console.log(`🔧 - data.homex: ${data.homex}, data.homey: ${data.homey}`);

        var list = data.scripts;
        //delete data.scripts;
        var spr;
        data.page = page;
        if (data.type == 'sprite') {
            mediaCount++;
            var fcn = function (spr) {
                console.log(`🎯 SPRITE CREATION CALLBACK triggered for: ${name}`);
                console.log(`🎯 - BEFORE setPos: spr.xcoor=${spr.xcoor}, spr.ycoor=${spr.ycoor}`);
                console.log(`🎯 - data to apply: data.xcoor=${data.xcoor}, data.ycoor=${data.ycoor}`);

                spr.setPos(data.xcoor, data.ycoor);

                console.log(`🎯 - AFTER setPos: spr.xcoor=${spr.xcoor}, spr.ycoor=${spr.ycoor}`);
                console.log(`🎯 - setPos completed with args: (${data.xcoor}, ${data.ycoor})`);

                mediaCount--;
                if (gn('backdrop').className == 'modal-backdrop fade in') {
                    Project.setProgress(Project.getMediaLoadRatio(70));
                }
                ScratchJr.log(spr.name, ScratchJr.getTime(), 'sec');
                console.log(`✅ Sprite ${name} recreated successfully`);
                console.log(`✅ - Final coordinates: (${spr.xcoor}, ${spr.ycoor})`);

                if (callBack) {
                    console.log(`🎯 Calling external callback for sprite: ${name}`);
                    callBack(spr);
                } else {
                    console.log(`🎯 No external callback provided for sprite: ${name}`);
                }
            };
            if (!data.defaultScale) {
                data.defaultScale = 0.5;
            }

            try {
                console.log(`🔧 CALLING new Sprite() constructor for: ${name}`);
                console.log(`🔧 - data.id: ${data.id}`);
                console.log(`🔧 - data.type: ${data.type}`);
                console.log(`🔧 - data.md5: ${data.md5}`);
                console.log(`🔧 - Sprite constructor available: ${typeof Sprite}`);

                spr = new Sprite(data, fcn);
                if (spr && data.role) {
                    spr.role = data.role;
                }
                console.log(`🔧 Sprite object created for: ${name}`);
                console.log(`🔧 - spr exists: ${!!spr}`);
                console.log(`🔧 - spr.div exists: ${!!(spr && spr.div)}`);
                console.log(`🔧 - spr.id: ${spr && spr.id}`);

                // load scripts - add error handling for missing script containers
                var scriptElement = gn(name + '_scripts');
                if (scriptElement && scriptElement.owner) {
                    var sc = scriptElement.owner;
                    for (var j = 0; j < list.length; j++) {
                        sc.recreateStrip(list[j]);
                    }
                    console.log(`🔧 Scripts loaded for sprite: ${name}`);

                    if (active) {
                        sc.activate();
                    } else {
                        sc.deactivate();
                    }
                } else {
                    // Scripts container missing - sprite might still be valid
                    // This can happen during import when DOM isn't fully ready
                    console.warn(`⚠️ Scripts container not found for sprite: ${name}, sprite created but scripts skipped`);

                    // Ensure the sprite is still properly initialized even without scripts
                    if (spr && spr.div) {
                        // Make sure the sprite is visible in the page
                        if (data.page && data.page.div) {
                            data.page.div.appendChild(spr.div);
                            console.log(`🔧 Sprite ${name} manually added to page`);
                        }
                    }
                }
            } catch (error) {
                console.error(`❌ Failed to recreate sprite ${name}:`, error.message);
                throw error;
            }
        } else {
            spr = new Sprite(data, callBack);
        }

        console.log(`\n🏁 FINAL SPRITE CHECK for ${name}:`);
        console.log(`🏁 - spr exists: ${!!spr}`);
        console.log(`🏁 - spr.div exists: ${!!(spr && spr.div)}`);
        console.log(`🏁 - spr.shown: ${spr && spr.shown}`);

        if (spr && spr.div) {
            spr.div.style.opacity = spr.shown ? 1 : 0;
            console.log(`🏁 - Opacity set to: ${spr.div.style.opacity}`);
            console.log(`🏁 - Sprite div parent: ${(spr.div.parentNode && spr.div.parentNode.id) || 'no parent'}`);
        } else {
            console.error(`❌ Sprite ${name} was not created properly - missing div`);
            if (spr) {
                console.error(`❌ - spr exists but missing div property`);
            } else {
                console.error(`❌ - spr object is null/undefined`);
            }
        }

        console.log(`🏁 RETURNING sprite: ${!!spr} for ${name}\n`);
        return spr;
    }

    //////////////////////////////////////////////////
    // Save project data
    //////////////////////////////////////////////////

    static prepareToSave (id, whenDone) {
        if (saving) {
            Alert.open(frame, gn('flip'), 'Waiting', '#28A5DA');
            Project.waitUntilSaved(id, whenDone);
        } else {
            Alert.open(frame, gn('flip'), 'Saving', '#28A5DA');
            Project.save(id, whenDone);
        }
    }

    static waitUntilSaved (id, fcn) {
        if (saving) {
            setTimeout(function () {
                Project.waitUntilSaved(id, fcn);
            }, 500);
        } else {
            Project.save(id, fcn);
        }
    }

    // Determine if thumbnailMD5 is unique to projectID
    // callback(true/false)
    static thumbnailUnique (thumbnailMD5, projectID, callback) {
        console.log('🔍 thumbnailUnique CHECK:', {
            checkingFile: thumbnailMD5,
            currentProject: projectID
        });
        var json = {};
        json.cond = 'deleted = ? AND id != ? AND gallery IS NULL';
        json.items = ['name', 'thumbnail', 'id'];
        json.values = ['NO', projectID];
        IO.query(OS.database, json, function (result) {
            var pdata = JSON.parse(result);
            console.log(`   Found ${pdata.length} other projects to check`);
            var isUnique = true;
            for (var p = 0; p < pdata.length; p++) {
                var thispdata = IO.parseProjectData(pdata[p]);
                var th = thispdata.thumbnail;
                if (th) {
                    var thumb = (typeof th == 'string') ? JSON.parse(th) : th;
                    if (thumb && thumb.md5) {
                        console.log(`   Project ${thispdata.id} uses thumbnail: ${thumb.md5}`);
                        if (thumb.md5 == thumbnailMD5) {
                            console.log(`   ❌ MATCH FOUND - thumbnail is NOT unique (shared by project ${thispdata.id})`);
                            isUnique = false;
                        }
                    }
                }
            }
            console.log(`   Result: isUnique = ${isUnique}`);
            callback(isUnique);
        });
    }

    static save (id, whenDone) {
        saving = true;
        var th = metadata.thumbnail;

        // Fix Issue #446: Remove old thumbnail BEFORE creating new one (singleton pattern)
        // ALWAYS remove the old thumbnail - we want exactly 1 thumbnail per project
        if (th && ScratchJr.editmode != 'storyStarter') {
            var thumb = (typeof th === 'string') ? JSON.parse(th) : th;
            // Fix Issue #30: Safety check for malformed thumbnails (empty arrays)
            if (thumb && thumb.md5 && thumb.md5.indexOf('samples/') < 0) {
                console.log('🗑️ REMOVING OLD THUMBNAIL:', thumb.md5, 'from project', id);
                OS.remove(thumb.md5, function (result) {
                    console.log('✅ Old thumbnail removed, proceeding with new thumbnail');
                    proceedWithNewThumbnail();
                });
                return; // Wait for callback before proceeding
            }
        }

        // No old thumbnail to remove, proceed immediately
        proceedWithNewThumbnail();

        function proceedWithNewThumbnail() {
            metadata.id = id;
            metadata.json = Project.getProject(ScratchJr.stage.pages[0].id);

            // DEBUG: Log what we're about to save
            console.log('💾 Project.save() - Serializing project data...');
            console.log('💾 Project ID:', id);
            console.log('💾 Pages:', metadata.json.pages);
            console.log('💾 Current page:', metadata.json.currentPage);

            // Log sprite data for debugging
            if (metadata.json.pages && metadata.json.pages[0]) {
                const firstPageId = metadata.json.pages[0];
                const firstPageData = metadata.json[firstPageId];
                if (firstPageData && firstPageData.sprites) {
                    console.log(`💾 Page ${firstPageId} has ${firstPageData.sprites.length} sprites`);
                    firstPageData.sprites.forEach((spriteId, idx) => {
                        const spriteData = firstPageData[spriteId];
                        if (spriteData) {
                            console.log(`💾 Sprite ${idx}: ${spriteId} at (${spriteData.xcoor}, ${spriteData.ycoor})`);
                        }
                    });
                }
            }

            Project.getThumbnailPNG(ScratchJr.stage.pages[0], 192, 144, getMD5);
        }
        function getMD5 (dataurl) {
            var pngBase64 = dataurl.split(',')[1];
            OS.getmd5(pngBase64, function (str) {
                // FIX: Pass the full data URL, not just the base64 part
                savePNG(str, dataurl);  // Changed from pngBase64 to dataurl
            });
        }

        function savePNG (md5, fullDataUrl) {  // Changed parameter name for clarity
            // Fix Issue #446: Construct filename with project prefix to avoid collisions
            var filename = ScratchJr.currentProject + '_' + md5;
            console.log('💾 SAVING NEW THUMBNAIL:', {
                constructedFilename: filename,
                projectId: ScratchJr.currentProject,
                md5Hash: md5,
                dataUrlLength: fullDataUrl.length  // Log for debugging
            });
            // FIX: Pass the full data URL so media-management.js can detect it's base64 PNG
            OS.setmedianame(fullDataUrl, filename, 'png', function(savedFilename) {
                doNext(savedFilename, fullDataUrl);
            });
        }

        function doNext (md5, thumbnailDataUrl) {
            // The md5 parameter is actually the full filename returned by io_setmedianame
            console.log('✅ NEW THUMBNAIL SAVED:', {
                returnedFilename: md5,
                willStoreInMetadata: md5
            });
            // Issue #687: Store both md5 reference (for local rendering) and dataURL (for CloudSync)
            metadata.thumbnail = {
                'pagecount': ScratchJr.stage.pages.length,
                'md5': md5,  // This should be the full filename (projectId_hash.png)
                'dataUrl': thumbnailDataUrl  // Store dataURL for CloudSync (avoids timing issues)
            };
            metadata.mtime = (new Date()).getTime().toString();
            IO.saveProject(metadata, saveDone);
        }

        function saveDone () {
            saving = false;
            // FIX Issue #263: Reset changed flag after successful save to prevent infinite auto-save loop
            ScratchJr.changed = false;
            if (whenDone) {
                whenDone();
            }
        }
    }

    static getProject (pageid) {
        var obj = {};
        obj.pages = ScratchJr.stage.getPagesID();
        obj.currentPage = pageid;
        for (var i = 0; i < ScratchJr.stage.pages.length; i++) {
            obj[ScratchJr.stage.pages[i].id] = ScratchJr.stage.pages[i].encodePage();
        }

        // CRITICAL FIX (Issue #447): Preserve existing media section if available
        // When a project with embedded media is loaded, we must preserve it
        // Otherwise it gets lost when localStorage is cleared (private mode)
        var preservedMedia = null;
        try {
            // Check if we have preserved media from the last load
            if (Project._lastLoadedMedia && Object.keys(Project._lastLoadedMedia).length > 0) {
                preservedMedia = Project._lastLoadedMedia;
                console.log(`📦 Preserving ${Object.keys(preservedMedia).length} media entries from last load`);
            }
        } catch (e) {
            // Non-critical - proceed with localStorage-based media
        }

        // Embed compact media for user assets actually used on pages (safety for Issue #47)
        try {
            var usedMd5 = {};
            // Collect backgrounds and sprite md5s actually used
            for (var p = 0; p < ScratchJr.stage.pages.length; p++) {
                var page = ScratchJr.stage.pages[p];
                if (page.md5) { usedMd5[page.md5] = true; }
                var spritelist = page.getSprites();
                for (var s = 0; s < spritelist.length; s++) {
                    var spr = gn(spritelist[s]).owner;
                    if (spr && spr.md5) { usedMd5[spr.md5] = true; }
                }
            }
            // Build media map: { key: { svg?: base64, png?: base64 } }
            var media = {};
            var isLibraryAsset = function(md5) {
                try { return (window.MediaLib && window.MediaLib.keys && (md5 in window.MediaLib.keys)); } catch (_) { return false; }
            };
            var getItem = function(key) { try { return localStorage.getItem(key); } catch (_) { return null; } };
            var tryStore = function(md5, filename) {
                var entry = media[md5] || {};
                // Prefer exact filename lookups in localStorage
                var svg = getItem('scratchjr_media_' + filename);
                if (!svg && filename.indexOf('.') < 0) { svg = getItem('scratchjr_media_' + filename + '.svg'); }
                if (!entry.svg) {
                    // Also check legacy key without extension
                    var svgLegacy = getItem('scratchjr_media_' + md5) || getItem('scratchjr_media_' + md5 + '.svg');
                    entry.svg = (svg || svgLegacy) || entry.svg;
                }
                var png = getItem('scratchjr_asset_' + filename) || getItem('scratchjr_media_' + filename);
                if (!png && filename.indexOf('.') < 0) {
                    png = getItem('scratchjr_asset_' + filename + '.png') || getItem('scratchjr_media_' + filename + '.png');
                }
                if (!entry.png) {
                    // Also check legacy key without extension
                    var pngLegacy = getItem('scratchjr_asset_' + md5) || getItem('scratchjr_media_' + md5) || getItem('scratchjr_asset_' + md5 + '.png');
                    entry.png = (png || pngLegacy) || entry.png;
                }
                if (entry.svg || entry.png) { media[md5] = entry; }
            };
            Object.keys(usedMd5).forEach(function(md5) {
                if (!md5 || isLibraryAsset(md5)) { return; }

                // FIRST: Check if we have preserved media for this asset
                if (preservedMedia && preservedMedia[md5]) {
                    media[md5] = preservedMedia[md5];
                    console.log(`✅ Using preserved media for: ${md5}`);
                } else {
                    // FALLBACK: Try to get from localStorage
                    tryStore(md5, md5);
                    ['svg','png','jpg','jpeg'].forEach(function(ext) { tryStore(md5, md5 + '.' + ext); });
                }
            });

            // Also include any preserved media that wasn't in usedMd5 (safety)
            if (preservedMedia) {
                Object.keys(preservedMedia).forEach(function(md5) {
                    if (!media[md5]) {
                        media[md5] = preservedMedia[md5];
                        console.log(`✅ Including additional preserved media: ${md5}`);
                    }
                });
            }

            if (Object.keys(media).length > 0) {
                obj.media = media;
            }
        } catch (e) {
            // Non-fatal; embedding is best-effort
        }

        return obj;
    }

    static getUndo () {
        return Project.getProject(ScratchJr.stage.currentPage.id);
    }

    static encodeSprite (name) {
        return gn(name).owner.getData();
    }

    static encodeStrip (b) {
        var res = [];
        var hasargs = ['playsnd', 'gotopage', 'playusersnd', 'setcolor', 'onmessage', 'message', 'setspeed'];
        var loops = ['repeat'];
        var carets = ['caretcmd', 'caretend', 'caretstart'];
        while (b != null) {
            var bt = b.blocktype;
            // Don't encode carets in a strip
            if (carets.indexOf(bt) > -1) {
                b = b.next;
                continue;
            }
            if (bt == 'caretrepeat') {
                // Convert repeat carets to actual repeats for the encoding
                bt = 'repeat';
            }
            var arg = (b.arg != null) || (hasargs.indexOf(bt) > -1) ? b.getArgValue() : null;
            if (!arg && (arg != 0)) {
                arg = 'null';
            }
            var dx = b.div.left / b.scale;
            var dy = b.div.top / b.scale;
            var data = [bt, arg, dx, dy];
            if (loops.indexOf(bt) > -1) {
                var inside = Project.encodeStrip(b.inside);
                data.push(inside);
            }
            res.push(data);
            b = b.next;
        }
        return res;
    }

    /////////////////////////////
    // Project PNG Thumbnail
    /////////////////////////////

    static getThumbnailPNG (page, w, h, fcn) {
        var scale = w / 480;
        var data = {};
        data.pagecount = ScratchJr.stage.pages.length;
        var c = document.createElement('canvas');
        setCanvasSize(c, w, h);
        var ctx = c.getContext('2d');
        var md5 = page.md5;
        ctx.fillStyle = window.Settings.stageColor;
        ctx.fillRect(0, 0, w, h);
        if (!md5) {
            Project.drawSprites(page, scale, c, w, h, fcn);
        } else {
            var pcnv;
            if (md5.substr(md5.length - 3) == 'png') {
                var bgimg = page.div.firstElementChild.firstElementChild;
                pcnv = Project.drawPNGInCanvas(bgimg, 480, 360);
            } else {
                pcnv = Project.drawSVGinCanvas(page.svg, 480, 360);
            }
            ctx.drawImage(pcnv, 0, 0, 480, 360, 0, 0, w, h);
            Project.drawSprites(page, scale, c, w, h, fcn);
        }
    }
    static drawPNGInCanvas (png, w, h) {
        var srccnv = document.createElement('canvas');
        setCanvasSize(srccnv, w, h);
        var ctx = srccnv.getContext('2d');
        ctx.drawImage(png, 0, 0, w, h);
        return srccnv;
    }

    static drawSVGinCanvas (extxml, w, h) {
        var srccnv = document.createElement('canvas');
        setCanvasSize(srccnv, w, h);
        var ctx = srccnv.getContext('2d');
        for (var i = 0; i < extxml.childElementCount; i++) {
            var elem = extxml.children ? extxml.children[i] : extxml.childNodes[i];
            if (elem) {
                SVG2Canvas.drawLayer(elem, ctx, SVG2Canvas.drawLayer);
            }
        }
        return srccnv;
    }

    static maskBorders (ctx, w, h) {
        ctx.save();
        ctx.globalCompositeOperation = 'destination-in';
        if (window.Settings.edition != 'PBS') {
            ctx.drawImage(BlockSpecs.projectThumb, 0, 0, w, h);
        }
        ctx.restore();
    }

    static drawSprites (page, scale, c, w, h, fcn) {
        var ctx = c.getContext('2d');
        doNext(1);
        function doNext (n) {
            if (!(n < page.div.childElementCount)) {
                Project.maskBorders(c.getContext('2d'), w, h);
                fcn(c.toDataURL('image/png'));
            } else {
                var spr = page.div.childNodes[n].owner;
                if (!spr || !spr.shown) {
                    doNext(n + 1);
                } else {
                    drawLoadedImage(page, ctx, spr.outline, spr, scale, n);
                }
            }
        }

        function drawLoadedImage (page, ctx, img, spr, scale, n) {
            page.drawSpriteImage(ctx, img, spr, scale);
            doNext(n + 1);
        }

    }

    static ensureActivityPluginsRegistered () {
        if (!ActivityPluginRegistry.has('challenge')) {
            ActivityPluginRegistry.register('challenge', new ChallengePlugin());
        }
        if (!ActivityPluginRegistry.has('guided-creation')) {
            ActivityPluginRegistry.register('guided-creation', new GuidedCreationPlugin());
        }
    }

    static destroyActivityExperience () {
        if (activityRunMonitor) {
            clearInterval(activityRunMonitor);
            activityRunMonitor = null;
        }
        activityRunObservedExecution = false;
        if (activeActivityUI) {
            activeActivityUI.destroy();
            activeActivityUI = null;
        }
        if (activeActivityController) {
            activeActivityController.destroy();
            activeActivityController = null;
        }
        try {
            delete window.__activityController;
            delete window.__activityUI;
        } catch (_) {}
    }

    static async initActivityExperience () {
        Project.destroyActivityExperience();
        Project.ensureActivityPluginsRegistered();

        const container = gn('stage') || gn('stageframe') || frame;
        if (!container) {
            return;
        }

        const controller = await ActivityController.fromURLParams(container);
        if (!controller) {
            return;
        }

        activeActivityController = controller;
        try {
            window.__activityController = controller;
        } catch (_) {}
        activeActivityUI = new ActivityUI(
            controller.activityData,
            controller.stateMachine,
            controller.plugin,
            container,
            {
                onReplay: function () {
                    Project.restartActiveChallengeActivity(null, {fromBeginning: Boolean(controller.activityData && (controller.activityData.storyWalk || controller.activityData.bugBand))});
                },
                onNext: controller.activityData && (controller.activityData.storyWalk || controller.activityData.bugBand)
                    ? function () {
                        ScratchJr.saveProject(null, ScratchJr.flippage);
                    }
                    : null,
                onComplete: function (payload) {
                    window.__lastActivityCompletion = payload;
                }
            }
        );

        controller.startActivity();

        // Bug Band fix: rebuild sprite thumbnails now that the activity controller
        // is registered on window.__activityController.  Earlier updateSprites() calls
        // during project load ran before the controller was set, so the bugBand bypass
        // in Thumbs.updateSprites was inactive and all Bug Band sprites (roles: drummer,
        // guitar, singer) were filtered out.  Without thumbnails, highlighSprite /
        // unhighlighSprite never fire, so ScriptsPane.setActiveScript is never called
        // and the selected sprite's scripts div stays visibility:hidden — causing
        // blocks dragged to the canvas to "disappear".
        if (controller.activityData && controller.activityData.bugBand) {
            try {
                Thumbs.updateSprites();
            } catch (_) {}
        }

        try {
            window.__activityUI = activeActivityUI;
        } catch (_) {}
    }

    static startActivityRunMonitor () {
        if (activityRunMonitor) {
            clearInterval(activityRunMonitor);
        }

        activityRunMonitor = setInterval(function () {
            if (!activeActivityController || !ScratchJr.runtime) {
                return;
            }

            const stateMachine = activeActivityController.stateMachine;
            if (!stateMachine || stateMachine.activityType !== 'challenge' || stateMachine.state !== 'RUNNING') {
                return;
            }

            if (!activityRunObservedExecution) {
                if (!ScratchJr.runtime.inactive()) {
                    activityRunObservedExecution = true;
                }
                return;
            }

            const plugin = activeActivityController.plugin;
            const allowValidationWhileRunning = plugin &&
                typeof plugin.shouldEvaluateWhileThreadsRunning === 'function' &&
                plugin.shouldEvaluateWhileThreadsRunning(ScratchJr.runtime);

            if (!ScratchJr.runtime.inactive() && !allowValidationWhileRunning) {
                return;
            }

            clearInterval(activityRunMonitor);
            activityRunMonitor = null;
            activityRunObservedExecution = false;
            ScratchJr.stopStrips();
            activeActivityController.evaluate();
        }, 120);
    }
}
